import { transformProductData } from "@/lib/content-service//helpers/transformProductData"
import { IGetProductsByIds } from "@/lib/content-service/interfaces/IProductService"
import { GetCategoryUrisRequest } from "@/lib/content-service/types/api/GetCategoryUrisRequest"
import { GetCategoryUrisResponse } from "@/lib/content-service/types/api/GetCategoryUrisResponse"
import { GetProductMetaDataRequest } from "@/lib/content-service/types/api/GetProductMetaDataRequest"
import { ProductByIdRequest } from "@/lib/content-service/types/api/ProductByIdRequest"
import { ProductByUriRequest } from "@/lib/content-service/types/api/ProductByUriRequest"
import { ProductsByIdsRequest } from "@/lib/content-service/types/api/ProductsByIdsRequest"
import { ProductsRequest } from "@/lib/content-service/types/api/ProductsRequest"
import {
    ProductData,
    ProductMetaDataResponse
} from "@/lib/content-service/types/ContentServiceProduct"
import { ClientFetchService } from "@/lib/http/ClientFetchService"
import { FetchService } from "@/lib/http/FetchService"
import Logger from "@/lib/logger/Logger"
import { Result, ResultFactory, ResultUtil } from "@/types/generic/Result"
import qs from "qs"

export class ContentServiceProductService implements IGetProductsByIds {
    constructor(
        private fetchService: FetchService | ClientFetchService,
        private logger: Logger
    ) {}

    /**
     * @param {ProductsByIdsRequest} req
     */
    public getProductsByIds = async (
        req: ProductsByIdsRequest
    ): Promise<Result<ProductData[]>> => {
        const { productIds, next, cache, ...queryParams } = req
        try {
            const response = await this.fetchService.fetch(
                `/products/ids/${productIds.trim()}?` + qs.stringify(queryParams),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${process.env.CONTENT_SERVICE_TOKEN}`,
                        "Content-Type": "application/json"
                    },
                    cache,
                    next
                }
            )
            if (ResultUtil.isSuccess(response) && Array.isArray(response.data)) {
                const productData = response.data
                return ResultFactory.success(
                    productData.map(({ data, product }) =>
                        transformProductData({ ...data, productId: product })
                    )
                )
            } else {
                return (
                    this.logger.error("Failed to fetch products by Ids"),
                    ResultFactory.failure(new Error("Failed to fetch products by Ids"))
                )
            }
        } catch (error) {
            return (
                this.logger.error(
                    `Server error for ID: ${productIds}, with message: ${error}`
                ),
                ResultFactory.failure(
                    error instanceof Error ? error : new Error("Unknown error")
                )
            )
        }
    }

    /**
     * @param {ProductsRequest} req
     */
    public getProducts = async (req: ProductsRequest): Promise<Result<ProductData[]>> => {
        const { next, cache, ...queryParams } = req
        try {
            const response = await this.fetchService.fetch(
                `/products?` + qs.stringify(queryParams),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${process.env.CONTENT_SERVICE_TOKEN}`,
                        "Content-Type": "application/json"
                    },
                    cache,
                    next
                }
            )

            if (response.success) {
                return ResultFactory.success(
                    (response.data as { data: ProductData[] }).data
                )
            } else {
                return (
                    this.logger.error("Failed to fetch products"),
                    ResultFactory.failure(new Error("Failed to fetch products"))
                )
            }
        } catch (error) {
            return (
                this.logger.error(`Error when fething /products with message: ${error}`),
                ResultFactory.failure(
                    error instanceof Error ? error : new Error("Unknown error")
                )
            )
        }
    }

    /**
     * @param {ProductByUriRequest} req
     */
    public getProductByUri = async (
        req: ProductByUriRequest
    ): Promise<Result<ProductData>> => {
        const { uri, next, cache, ...queryParams } = req
        try {
            const response = await this.fetchService.fetch(
                `/product/uri/${uri}?` + qs.stringify(queryParams),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${process.env.CONTENT_SERVICE_TOKEN}`,
                        "Content-Type": "application/json"
                    },
                    cache,
                    next
                }
            )
            if (ResultUtil.isSuccess(response)) {
                const productData = (response.data as { data: ProductData }).data
                return ResultFactory.success(transformProductData(productData))
            } else {
                return ResultFactory.failure(
                    new Error("Failed to fetch product by URI for: " + uri)
                )
            }
        } catch (error) {
            return ResultFactory.failure(
                error instanceof Error ? error : new Error("Unknown error")
            )
        }
    }

    /**
     * @param {ProductByIdRequest} req
     */
    public getProductById = async (
        req: ProductByIdRequest
    ): Promise<Result<ProductData>> => {
        const { productId, next, cache, ...queryParams } = req
        try {
            const response = await this.fetchService.fetch(
                `/product/id/${productId}?` + qs.stringify(queryParams),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${process.env.CONTENT_SERVICE_TOKEN}`,
                        "Content-Type": "application/json"
                    },
                    cache,
                    next
                }
            )

            if (response.success) {
                return ResultFactory.success(
                    (response.data as { data: ProductData }).data
                )
            } else {
                return (
                    this.logger.error(`Failed to fetch product by Id for: ${productId}`),
                    ResultFactory.failure(
                        new Error(`Failed to fetch product by Id for: ${productId}`)
                    )
                )
            }
        } catch (error) {
            return ResultFactory.failure(
                error instanceof Error ? error : new Error("Unknown error")
            )
        }
    }

    /**
     * This is used to get the meta data for the product page, title, description, media, etc.
     * But also to get the correct hrefs on available markets, the api is also basing this on the redirect list.
     * If there's a redirect it would be: en-us/product-NA-A -> en/product-EU-A (the API is checking availability on the destination redirect as well)
     * @param {GetProductMetaDataRequest} req
     */
    public getProductMetaData = async (
        req: GetProductMetaDataRequest
    ): Promise<Result<ProductMetaDataResponse>> => {
        const { uri, next, cache, ...queryParams } = req
        try {
            const response = await this.fetchService.fetch(
                `/product/uri/${uri}/metadata?` + qs.stringify(queryParams),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${process.env.CONTENT_SERVICE_TOKEN}`,
                        "Content-Type": "application/json"
                    },
                    cache,
                    next
                }
            )

            if (ResultUtil.isSuccess(response)) {
                const productMetaData = (
                    response.data as { data: ProductMetaDataResponse }
                ).data
                return ResultFactory.success(productMetaData)
            } else {
                return ResultFactory.failure(
                    new Error("Failed to fetch product by URI for: " + uri)
                )
            }
        } catch (error) {
            return ResultFactory.failure(
                error instanceof Error ? error : new Error("Unknown error")
            )
        }
    }

    /**
     * @param {GetCategoryUrisRequest} req
     */
    public getCategoryUris = async (
        req: GetCategoryUrisRequest
    ): Promise<Result<GetCategoryUrisResponse>> => {
        const { skus, next, cache, ...queryParams } = req
        try {
            const response = await this.fetchService.fetch(
                `/products/skus/${skus}/category-uris?` + qs.stringify(queryParams),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${process.env.NEXT_PUBLIC_CONTENT_SERVICE_TOKEN}`,
                        "Content-Type": "application/json"
                    },
                    cache,
                    next
                }
            )
            if (ResultUtil.isSuccess(response)) {
                return ResultFactory.success(response.data as GetCategoryUrisResponse)
            } else {
                return ResultFactory.failure(
                    new Error("Failed to fetch category URIs for SKUs: " + skus)
                )
            }
        } catch (error) {
            return ResultFactory.failure(
                error instanceof Error ? error : new Error("Unknown error")
            )
        }
    }
}
